import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";

import { formatVolume } from "@utils/formatVolume";

/** How much of a volume is used, as a bar with both figures beneath. Volumes are in litres. */
const VolumeBar: React.FunctionComponent<{
  used: number;
  total: number;
  className?: string;
}> = ({ used, total, className }) => {
  const { t } = useTranslation();
  const figuresId = useId();
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;

  return (
    <div className={twMerge("flex flex-col gap-1", className)}>
      <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
        <div
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct * 10) / 10}
          aria-labelledby={figuresId}
          className={classNames(
            "h-full rounded-full transition-all duration-500",
            pct > 90
              ? "bg-red-500"
              : pct > 75
              ? "bg-amber-500"
              : "bg-primary-1",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div
        id={figuresId}
        className="flex justify-between gap-2 text-xxs text-gray-500"
      >
        <span>
          {t("zenith.ui.volume_used", { volume: formatVolume(used) })}
        </span>
        <span>
          {t("zenith.ui.volume_total", { volume: formatVolume(total) })}
        </span>
      </div>
    </div>
  );
};

export default VolumeBar;
